import {ErrorHandler, inject, NgModule, provideAppInitializer} from "@angular/core";
import {AppComponent} from './app.component';
import {AuthConfig, OAuthModule, OAuthStorage} from 'angular-oauth2-oidc';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {ToastrModule} from 'ngx-toastr';
import {RouterModule} from '@angular/router';
import {AppRoutes} from './app-routes';
import {AuthInterceptor} from '../app-core/security/auth.interceptor';
import {AplicationErrorHandler} from '../app-core/service/error-handler';
import {BrowserModule} from '@angular/platform-browser';
import {GoogleMapsModule} from '@angular/google-maps';
import {LoginComponent} from './login/login.component';
import {environment} from '../../environments/environment';
import {LoginService} from '../app-core/service/login.service';
import {lastValueFrom} from 'rxjs';
import {ScriptLoaderService} from '../app-core/service/script-loader.service';
import {DeprecatedSharedUtilsModule} from '../app-core/utils/deprecated-shared-utils-module';
import {provideNgxMask} from 'ngx-mask';
import {provideAnimations} from '@angular/platform-browser/animations';

export function oAuthStorageFactory(): OAuthStorage {
    return localStorage;
}


export function getAuthCodeFlowConfig() {
    const urlPortal = window.location.origin + '/';

    return {
        issuer: `${environment.keycloak.url}/realms/${environment.keycloak.realm}`,
        redirectUri: urlPortal,
        clientId: environment.keycloak.clientId,
        responseType: 'code',
        scope: 'openid profile email offline_access',
        showDebugInformation: true,
        timeoutFactor: 0.7,
        silentRefreshRedirectUri: `${urlPortal}silent-renew.html`,
        endSessionEndpoint: `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/logout`,
        postLogoutRedirectUri: urlPortal,
    };
}

const PUBLIC_PATHS = ['privacidade'];

export function appInitializerFactory(
    loginService: LoginService,
    scriptLoaderService: ScriptLoaderService,
    authConfig: AuthConfig
): () => Promise<any> {
    return async () => {
        const currentPath = window.location.pathname.replace(/^\//, '').split('/')[0];
        const isPublicRoute = PUBLIC_PATHS.includes(currentPath);

        await lastValueFrom(scriptLoaderService.loadMultipleScripts([
            `assets/styles/demo-12/style.bundle.min.css`,
            `https://www.googletagmanager.com/gtag/js?id=UA-173921915-1`,
            `https://ajax.googleapis.com/ajax/libs/webfont/1.6.16/webfont.js`
        ]));

        if (isPublicRoute) {
            return Promise.resolve();
        }

        loginService.oidcSecurityService.configure(authConfig);

        try {
            const isLoggedIn = await loginService.oidcSecurityService.loadDiscoveryDocumentAndTryLogin();
            if (!isLoggedIn) {
                return Promise.resolve();
            }
            loginService.oidcSecurityService.setupAutomaticSilentRefresh();
            try {
                const authedUser = await lastValueFrom(loginService.doAuth());
                if (!authedUser) {
                    throw new Error('Sem autorização do backend');
                }
                await loginService.keycloackRefreshToken();
                loginService.setUsuarioLogado(authedUser);
            } catch (authError) {
                loginService.logout(true);
            }
        } catch (error) {
            loginService.logout(true);
        }
        return Promise.resolve();
    };
}

@NgModule({
    declarations: [
        LoginComponent,
        AppComponent
    ],
    imports: [
        BrowserModule,
        GoogleMapsModule,
        DeprecatedSharedUtilsModule,
        OAuthModule.forRoot(),
        ToastrModule.forRoot({
            closeButton: true,
            progressBar: true,
            maxOpened: 1,
            preventDuplicates: true,
            autoDismiss: false,
            toastClass: 'ui-toast'
        }),
        RouterModule.forRoot(AppRoutes)
    ],
    providers: [
        provideHttpClient(withInterceptors([AuthInterceptor])),
        {provide: ErrorHandler, useClass: AplicationErrorHandler},
        {provide: OAuthStorage, useFactory: oAuthStorageFactory},
        {provide: AuthConfig, useValue: getAuthCodeFlowConfig()},
        // {provide: LocationStrategy, useClass: HashLocationStrategy},
        provideNgxMask(),
        provideAnimations(),
        provideAppInitializer(() => {
            const loginService = inject(LoginService);
            const scriptLoaderService = inject(ScriptLoaderService);
            const authConfig = inject(AuthConfig);
            return appInitializerFactory(loginService, scriptLoaderService, authConfig)();
        }),
    ],
    bootstrap: [AppComponent]
})
export class AppModule {

}
